import { useMemo, type FC } from 'react';
import ExpandButton from 'components/molecules/expand-button';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';
import { useTranslation } from 'react-i18next';
import { languagesMap } from 'constants/languages';
import classNames from 'classnames';
import useUser from 'utils/useUser';
import ExclamationTriangleIcon from 'components/atoms/icons/exclamation-triangle';
import { Flex, Typography, Divider } from 'antd';
import { CheckCircleFilled, StopFilled } from '@ant-design/icons';
import UserAvatar from 'components/atoms/user-avatar';
import {
  type CcmStatuses,
  DateFormats,
  type RpmStatuses,
  type Languages,
  type ClinicDoNotCallRestrictionType,
  type DoNotCallRestrictionType,
} from 'infrastructure/enums';
import { DateFormatter } from 'infrastructure/functions';
import ProgramType from 'components/molecules/oneplaform/program-type';

import s from './styles.module.scss';
import DncTag from './dnc-tag';

import type { Nullable } from 'infrastructure/types';

const { Text } = Typography;

interface PatientCellProps {
  guid: string;
  name: string;
  dob: string;
  expand: boolean;
  language: Languages;
  provider: string;
  pmpmStatus?: boolean | null;
  interactionStatus?: boolean;
  rpmStatus?: RpmStatuses;
  ccmStatus?: CcmStatuses;
  isLoadingProgramType?: boolean;
  doNotCallRestrictionType?: Nullable<DoNotCallRestrictionType>;
  doNotCallStartTime?: Nullable<string>;
  doNotCallEndTime?: Nullable<string>;
  clinicDoNotCallRestrictionType?: Nullable<ClinicDoNotCallRestrictionType>;
  clinicDoNotCallStartTime?: Nullable<string>;
  clinicDoNotCallEndTime?: Nullable<string>;
  timezone?: Nullable<string>;
  onExpand: () => void;
}

const PatientCell: FC<PatientCellProps> = ({
  guid,
  name,
  provider,
  dob,
  language,
  expand,
  pmpmStatus,
  interactionStatus,
  rpmStatus,
  ccmStatus,
  isLoadingProgramType,
  onExpand,
  doNotCallRestrictionType,
  doNotCallStartTime,
  doNotCallEndTime,
  clinicDoNotCallRestrictionType,
  clinicDoNotCallStartTime,
  clinicDoNotCallEndTime,
  timezone,
}) => {
  const { isCriticalOrAtRiskAlertingRole, isAgencyDoctor } = useUser();
  const { t } = useTranslation();
  const showCROBadge = pmpmStatus && isAgencyDoctor && false; // false - to hide this flag

  const { firstName, lastName } = useMemo(() => {
    const nameParts = name.split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
    };
  }, [name]);

  const birthDate = useMemo(() => {
    return DateFormatter({
      date: dob || '',
      format: DateFormats['MM/dd/yyyy'],
    });
  }, [dob]);

  return (
    <Flex className={s.wrapper} align="center" gap={20}>
      <ExpandButton expanded={expand} onClick={onExpand} />

      <Flex align="center" gap={12}>
        <UserAvatar
          firstName={firstName}
          lastName={lastName}
          size="large"
          className={s['avatar-button']}
        />

        <Flex vertical className={s['patient-info-wrapper']} gap={4}>
          <Flex align="center" gap={12} wrap="wrap">
            <Text
              className={classNames(s.name, {
                [s.disabled]: isCriticalOrAtRiskAlertingRole,
              })}
            >
              <Link to={Routes.patientGuid(guid)}>{name}</Link>
            </Text>
            <ProgramType
              rpmStatus={rpmStatus}
              ccmStatus={ccmStatus}
              isLoading={isLoadingProgramType}
            />
            <Flex
              align="center"
              gap={8}
              className={classNames(
                s['two-way-sync'],
                interactionStatus ? s.sync : s.unsync,
              )}
            >
              {interactionStatus ? <CheckCircleFilled /> : <StopFilled />}
              <Text>
                {t('labels.2waySync')} -{' '}
                {interactionStatus ? t('labels.yes') : t('labels.no')}
              </Text>
            </Flex>
          </Flex>

          <Flex align="center" gap={6}>
            <Text className={s.date}>{birthDate}</Text>
            <Divider type="vertical" className={s.divider} />
            <Text className={s.language}>{languagesMap[language]}</Text>
            <Divider type="vertical" className={s.divider} />
            <Text className={s.provider}>{provider}</Text>
          </Flex>

          <Flex align="center" gap={8}>
            <DncTag
              doNotCallRestrictionType={doNotCallRestrictionType}
              doNotCallStartTime={doNotCallStartTime}
              doNotCallEndTime={doNotCallEndTime}
              clinicDoNotCallRestrictionType={clinicDoNotCallRestrictionType}
              clinicDoNotCallStartTime={clinicDoNotCallStartTime}
              clinicDoNotCallEndTime={clinicDoNotCallEndTime}
              patientTimezone={timezone}
            />
            {showCROBadge && (
              <Flex align="center" gap={4} className={s.cro}>
                <Text>{t('labels.cro')}</Text>
                <ExclamationTriangleIcon />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default PatientCell;
